import { sharedGet } from './sharedGet';
import { useEffect, useMemo, useState } from 'react';
import { useSubmodels } from './listingStore';
import { useSubmodelFilter } from './submodelFilterStore';
import { directionRank } from './viewDirections';
import { useStoredPhotosEpoch } from './listingPhotosSave';

// THE PICTURES OF THE FINISHED PRODUCT, for the submodel on screen — the notice's own
// listing photographs, kept in the database in Phase 1.
//
// Two phases read exactly this set and read it the same way. Phase 2's parts table offers
// them to the image generator as CONTEXT ("this is what the chair looks like, now draw its
// leg"); Phase 5 reconstructs the whole product from one of them. They are the same
// photographs in the same order with the same labels, and two copies of that would be two
// answers to "which pictures is this submodel" that could drift apart.
//
// Which submodel is a shared value (see submodelFilterStore), so nothing here takes it as
// an argument: the dropdown at the top of the phase is what these follow.

// One kept product photo: its id in `notice_listing_images` — what a run is made FROM —
// the merchant URL it was harvested at, and the side of the product Phase 1 says it shows.
// `view` is undefined for a picture Phase 1 never classified, which is not the same as a
// picture it classified as no particular view.
export type ContextPhoto = { id: number; url: string; view?: string };

// Which side of the product each of a submodel's pictures shows — Phase 1's answers,
// looked up by merchant URL, which is what both sides have in common. The context row and
// the Phase 1 strip are the same photographs, so they carry the same labels and are read
// in the same order; a picture that is "back" in one and unlabelled in the other would be
// two facts about one thing.
//
// Only the pictures Phase 1 actually judged go in. A strip it never classified has an
// EMPTY galleryViews, and filling the gaps with 'other' turned "nobody looked" into
// "looked at, and it is no view" for every photograph at once — a label under all ten of
// them, and, in the 3D pipeline, all ten deselected by a rule meant for room scenes.
export function useGalleryViews(docId: string): Map<string, string> {
  const submodels = useSubmodels(docId);
  const submodelFilter = useSubmodelFilter(docId);
  const submodel = submodels.find((s) => s.id === submodelFilter);
  return useMemo(() => viewsOf(submodel), [submodel]);
}

type Submodel = ReturnType<typeof useSubmodels>[number];
const viewsOf = (submodel: Submodel | undefined): Map<string, string> => {
  const m = new Map<string, string>();
  submodel?.gallery.forEach((u, i) => {
    const v = submodel.galleryViews[i];
    if (v) m.set(u, v);
  });
  return m;
};

// The chosen submodel's stored photographs, in the order Phase 1 shows them: front, back,
// the sides, then what is no view at all. The sort is stable, so an unclassified set keeps
// the order it arrived in.
//
// The fetch asks for the notice's photographs and not the submodel's: the route answers
// for the whole document, and re-asking it every time the dropdown moves would be a
// request per colour for a list that did not change.
type StoredPhoto = { id: number; variant: string; url: string };

// THE LAST ANSWER, per document, kept for the page's life: three panels read this list, and a
// panel mounting again starts from what is known rather than from nothing.
const lastStored = new Map<string, StoredPhoto[]>();

// `photos` is NULL UNTIL THE SERVER HAS ANSWERED. It started as `[]` — the same value as "no
// photographs kept" — so for a round trip Phase 5 said the product had no photographs, and a
// failed fetch said the same thing for good. `failed`: the last ask did not come back; what was
// known before it stays.
export function useSubmodelPhotos(docId: string): {
  photos: ContextPhoto[] | null;
  failed: boolean;
  // ANY submodel's, for a run that covers more than the one chosen (see MaterialsPanel's
  // "all submodels"): the same list, filtered and ordered as `photos` is.
  photosOf: (sub: string) => ContextPhoto[] | null;
} {
  const [state, setState] = useState<{ docId: string; stored: StoredPhoto[] | null; failed: boolean }>(
    () => ({ docId, stored: lastStored.get(docId) ?? null, failed: false }),
  );
  const submodels = useSubmodels(docId);
  const submodelFilter = useSubmodelFilter(docId);
  // Read again whenever photographs were saved without being asked (see listingPhotosSave).
  const savedEpoch = useStoredPhotosEpoch(docId);

  useEffect(() => {
    let cancelled = false;
    sharedGet<{ images: { id: number; url: string; variant?: string }[] }>(
        `/api/notice/listing-images/${encodeURIComponent(docId)}`,
      )
      .then(({ data }) => {
        // The merchant URL rides along: it is what Phase 1 filed the picture's direction
        // under, and the only thing tying this list back to that answer.
        const stored = (data.images || []).map((i) => ({
          id: i.id, variant: i.variant || '', url: i.url || '',
        }));
        lastStored.set(docId, stored);
        if (!cancelled) setState({ docId, stored, failed: false });
      })
      .catch(() => {
        if (!cancelled) {
          setState((was) => ({
            docId, stored: was.docId === docId ? was.stored : lastStored.get(docId) ?? null, failed: true,
          }));
        }
      });
    return () => { cancelled = true; };
  }, [docId, savedEpoch]);

  // Another document's answer is not this one's: until this one's lands, what is known of it.
  const now = state.docId === docId ? state : { stored: lastStored.get(docId) ?? null, failed: false };
  const stored = now.stored;
  const photosOf = useMemo(() => (sub: string): ContextPhoto[] | null => {
    if (!stored) return null;
    const views = viewsOf(submodels.find((s) => s.id === sub));
    return stored
      .filter((p) => p.variant === sub)
      .slice()
      .sort((a, b) => directionRank(views.get(a.url)) - directionRank(views.get(b.url)))
      .map((p) => ({ id: p.id, url: p.url, view: views.get(p.url) }));
  }, [stored, submodels]);
  const photos = useMemo(() => photosOf(submodelFilter), [photosOf, submodelFilter]);
  return { photos, failed: now.failed, photosOf };
}
